/**
 * A `YYYY-MM-DD` date as `5 sept 2026`, without timezone shifts.
 *
 * @param {string | null | undefined} value -- The ISO date.
 * @returns The formatted date, or an empty string.
 */
export const formatDate = (value: string | null | undefined): string => {
  if (!value) return '';
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat('es-CO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(Date.UTC(year, month - 1, day, 12)));
};

/**
 * Days between two ISO dates (end exclusive), never negative.
 *
 * @param {string} from -- Start date.
 * @param {string} to -- End date.
 * @returns The day count.
 */
export const daysBetween = (from: string, to: string): number =>
  Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000));

/**
 * A `YYYY-MM` period label as `febrero de 2027`, without timezone shifts.
 *
 * @param {string} periodLabel -- The month label.
 * @returns The formatted month, or the label itself when it is not a month.
 */
export const formatMonth = (periodLabel: string): string => {
  const [year, month] = periodLabel.slice(0, 7).split('-').map(Number);
  if (!year || !month) return periodLabel;
  return new Intl.DateTimeFormat('es-CO', { month: 'long', year: 'numeric' }).format(
    new Date(Date.UTC(year, month - 1, 1, 12)),
  );
};

/**
 * The `YYYY-MM` labels of every month between two ISO dates, both months included (a school
 * year from `2027-01-25` to `2027-11-30` gives `2027-01` … `2027-11`).
 *
 * @param {string} from -- Start date.
 * @param {string} to -- End date.
 * @returns The month labels in order; empty when `to` precedes `from`.
 */
export const monthsBetween = (from: string, to: string): string[] => {
  const [fromYear, fromMonth] = from.slice(0, 7).split('-').map(Number);
  const [toYear, toMonth] = to.slice(0, 7).split('-').map(Number);
  if (!fromYear || !fromMonth || !toYear || !toMonth) return [];
  const months: string[] = [];
  for (
    let year = fromYear, month = fromMonth;
    year < toYear || (year === toYear && month <= toMonth);
  ) {
    months.push(`${year}-${String(month).padStart(2, '0')}`);
    if (month === 12) {
      year += 1;
      month = 1;
    } else month += 1;
  }
  return months;
};
